//////////////////////////////////////////////////////////////////////
// Configurations related to search                                 //
// Note: some search experience is also on defaultMapsConfig        //
// and defaultListingConfig.js is responsible public data filtering //
//////////////////////////////////////////////////////////////////////

// NOTE: if you want to change the structure of the data,
// you should also check src/util/configHelpers.js
// some validation is added there.

// Main search used in Topbar.
// This can be either 'keywords' or 'location'.
// Note: The mainSearch comes from the listing-search asset nowadays by default.
//       To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
//
// usedemoto: 'keywords'. Buyers search for "Sur-Ron Light Bee" or "72v controller", not for a
// place. Used emotos and parts are shipped nationwide, and the 'inquiry' listing type is the only
// one with a location tab (default-purchase listings never collect a location), so a location-first
// search would return almost nothing.
export const mainSearch = {
  searchType: 'keywords',
};

/**
 * Configuration for default filters.
 * These are custom configs for each filter.
 * Common properties: key, schemaType, and label.
 * Note: the order of default filters is defined in util/configHelpers.js
 * To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
 */

// usedemoto: ENABLED.
// Justification: there are three listing types and two of them ('emoto-sale', 'parts-sale') describe
// fundamentally different products, while 'inquiry' is a not-for-checkout variant that appears in
// both trees. Buyers who want to buy now vs. buyers browsing inquiry-only listings are different
// intents, so the toggle earns its space. It queries pub_listingType, which requires the
// listingType search schema to be set (see docs/CONSOLE_SETUP.md).
// Note: mergeSearchConfig only includes this filter when `enabled` is an actual boolean.
export const listingTypeFilter = {
  enabled: true,
  schemaType: 'listingType',
  // schemaType, key, and other built-in config values are completely filled in configHelper.js
};

// usedemoto: ENABLED — this is the essential filter for this marketplace.
// Every emoto-specific and part-specific listing field is limited to a top-level category
// ('emotos' or 'parts'), and SearchPage hides category-limited filters until a category is chosen.
// Without this filter, a shopper on a bare /s page can never reveal brand, condition, part type etc.
// Note: mergeSearchConfig also requires categoryConfiguration.categories to be non-empty, so this
// filter only renders once the hosted listing-categories.json asset (or the local dev fallback
// in configListing.js) supplies a tree.
export const categoryFilter = {
  enabled: true,
  schemaType: 'category',
  // schemaType, key, and other built-in config values are completely filled in configHelper.js
};

// usedemoto: DISABLED.
// Neither default-purchase ('emoto-sale', 'parts-sale') nor default-inquiry uses availability, so
// no listing on this marketplace has an availability plan. Leaving this filter on renders a "Dates"
// control that sends availability params (see datesSearchParams in SearchPage.duck.js) which no
// listing can satisfy — picking any date range returns zero results.
// Note: an earlier comment here claimed this filter could not be turned off because
// "mergeSearchConfig passes it through unconditionally". That is wrong: mergeSearchConfig does put
// the object into the defaultFilters array, but validDefaultFilters then calls validDatesConfig,
// which returns null for `enabled: false`, and the trailing filter(Boolean) drops it. Verified by
// executing mergeConfig. Re-enable this (and set it in Console) only if a booking listing type is
// ever added.
export const dateRangeFilter = {
  enabled: false,
  schemaType: 'dates',
  // Should the entire date range be available, or just part of it
  // Note: Since we don't enforce location search for dates filtering,
  //       we don't use API's 'time-full' in actual queries. It would require time zone info.
  availability: 'time-full', // time-partial
  // Options: day/night. This affects counting and whether single day picking is possible.
  dateRangeMode: 'day',
};

/**
 * Note: the order of default filters is defined in util/configHelpers.js
 * To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
 */
// usedemoto price range.
// The upstream default (max 1000) is unusable here: validPriceConfig clamps URL params to
// [min, max], so a $4,500 Sur-Ron or a $13,000 Stark Varg would be unreachable by the slider.
// This range spans a $50 used tube up to a flagship bike. Step 50 keeps the slider usable.
// Reminder: mergeSearchConfig prefers the hosted listing-search.json asset whenever it has a
// mainSearch key, so this same range must also be set in Console (docs/CONSOLE_SETUP.md).
export const priceFilter = {
  schemaType: 'price',
  // Note: unlike most prices this is not handled in subunits
  min: 0,
  max: 15000,
  step: 50,
};

// usedemoto: ENABLED.
// Free-text is how people actually shop for used bikes and parts ("ultra bee", "sprocket 428",
// a model name, a part number). Three of our listing fields are deliberately NOT indexed
// ('model', 'knownIssues', 'partNumber') because there is no text filter UI for them; keyword
// search over title and description is the intended way to reach those listings.
// Note: mergeSearchConfig only reads `enabled === true` from the hosted asset, but it falls back
// to this whole object when there is no hosted search asset, so both paths need to be valid.
export const keywordsFilter = {
  key: 'keywords',
  schemaType: 'keywords',
  enabled: true,
};

// usedemoto sort options.
// The four built-in options are exactly right for a used-goods marketplace: newest first is the
// default browse order for a feed of one-off used bikes, and price sorting is how buyers hunt
// deals. 'relevance' is kept because the keywords filter above is enabled — validSortConfig keeps
// any option with a key plus a label/labelTranslationKey, and the SearchPage only surfaces
// relevance while a keyword query is active.
// conflictingFilters is left empty on purpose: sorting by price while keyword-searching is a
// legitimate and common combination here ("cheapest Light Bee").
// Not added: a rating sort (`meta_rating`) — it needs review data that a new marketplace does not
// have yet, and an option pointing at an unpopulated metadata key sorts arbitrarily.
export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Marketplace API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Relevance key is used with keywords filter.
  // Keywords filter also sorts results according to relevance.
  relevanceFilter: 'keywords',

  // Keyword filter is sorting the results by relevance.
  // If keyword filter is active, one might want to disable other sorting options
  // by adding 'keywords' to this list.
  conflictingFilters: [],

  options: [
    // These are default sort options
    { key: 'createdAt', labelTranslationKey: 'SortBy.newest' },
    { key: '-createdAt', labelTranslationKey: 'SortBy.oldest' },
    { key: '-price', labelTranslationKey: 'SortBy.lowestPrice' },
    { key: 'price', labelTranslationKey: 'SortBy.highestPrice' },
    // If you add own sort options, you can also use label key: { key: 'meta_rating', label: 'Highest rated' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    {
      key: 'relevance',
      labelTranslationKey: 'SortBy.relevance',
      labelTranslationKeyLong: 'SortBy.relevanceLong',
    },
  ],
};
